/**
 * @fileoverview Tenant resource API calls.
 * @description Wraps GET /sales/tenants, GET /sales/tenants/:id, POST /sales/tenants, and POST
 * /sales/tenants/:id/archive|restore — the bank records every sales page reads; see
 * docs/sales-tenant-api-contract.md for the endpoint contract.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

import type { TenantDetail } from '@/features/sales/mocks/tenants-mock.service'
import type { Tenant } from '@/features/sales/types/tenant.types'

export interface TenantListFilters {
  status?: Tenant['status']
  ownerId?: string
  ownerIds?: string[]
  previousOwnerId?: string
  tier?: Tenant['tier']
  state?: string
  bankType?: Tenant['bankType']
  archived?: boolean
  /** 'approved' (the default) or 'pending' — prospects awaiting approval are only ever listed
   * when asked for explicitly. */
  approval?: 'approved' | 'pending'
}

export interface CreateTenantRequest {
  name: string
  bankType: Tenant['bankType']
  headOffice: string
  regNumber: string
  state: string
  city: string
  address: string
  district: string
  pincode: string
  website: string
  branches: number
  employees: number | null
  coreBanking: string
  existingMis: string
  authorisedContact: Tenant['authorisedContact']
  leadSource: string
  leadRating: Tenant['leadRating']
  notes: string
  /** The assignee (owner), if any — set at once for a Super Admin's or Sales Head's prospect, on
   * approval for a Sales Manager's; a Sales Executive's is always their own. No target is created:
   * that's applied separately via Assign Target. */
  ownerId?: string | null
  /** Who is creating it — decides whether it needs a Sales Head's approval. */
  actingUserId: string
  actingUserRole: string
}

export interface ApproveTenantRequest {
  actingUserId: string
  actingUserRole: string
}

export interface UpdateTenantRequest {
  name?: string
  bankType?: Tenant['bankType']
  headOffice?: string
  regNumber?: string
  state?: string
  city?: string
  address?: string
  district?: string
  pincode?: string
  website?: string
  branches?: number
  employees?: number | null
  coreBanking?: string
  existingMis?: string
  authorisedContact?: Tenant['authorisedContact']
  leadSource?: string
  leadRating?: Tenant['leadRating']
  notes?: string
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Fetches tenants matching the supplied filters.
 * @param {TenantListFilters} [filters] - The filters to apply; an array filter (`ownerIds`) is
 * sent as a comma-joined query param.
 * @returns {Promise<Tenant[]>} Every matching tenant.
 */
export async function getTenants(filters: TenantListFilters = {}): Promise<Tenant[]> {
  const { ownerIds, ...rest } = filters
  const { data } = await httpClient.get<Tenant[]>('/sales/tenants', {
    params: { ...rest, ownerIds: ownerIds?.join(',') },
  })
  return data
}

/**
 * @description Fetches one tenant's "View Tenant" detail payload.
 * @param {string} id - The tenant id to fetch.
 * @returns {Promise<TenantDetail>} The tenant's profile plus its activity count.
 */
export async function getTenant(id: string): Promise<TenantDetail> {
  const { data } = await httpClient.get<TenantDetail>(`/sales/tenants/${id}`)
  return data
}

/**
 * @description Creates a new prospect/tenant record.
 * @param {CreateTenantRequest} input - The prospect's captured bank-identity fields.
 * @returns {Promise<Tenant>} The created tenant.
 */
export async function createTenant(input: CreateTenantRequest): Promise<Tenant> {
  const { data } = await httpClient.post<Tenant>('/sales/tenants', input)
  return data
}

/**
 * @description Updates a prospect's permitted profile fields. Only permitted before client
 * conversion (`status: 'Prospect'`) and only for the current owner or a supervisor in scope — see
 * updateTenant's own doc comment in tenants-mock-store.ts.
 * @param {string} id - The tenant id to update.
 * @param {UpdateTenantRequest} input - The fields to change, plus the editing user's id and role.
 * @returns {Promise<Tenant>} The updated tenant.
 */
export async function updateTenant(id: string, input: UpdateTenantRequest): Promise<Tenant> {
  const { data } = await httpClient.patch<Tenant>(`/sales/tenants/${id}`, input)
  return data
}

/**
 * @description Archives a tenant with a mandatory reason note.
 * @param {string} id - The tenant id to archive.
 * @param {string} reason - The mandatory reason note.
 * @returns {Promise<Tenant>} The updated tenant.
 */
/**
 * @description Approves a prospect waiting for its Sales Head (or Super Admin), which also makes the
 * assignee its maker chose, if any, its owner (still without a target).
 * @param {string} id - The pending prospect.
 * @param {ApproveTenantRequest} input - Who is approving it.
 * @returns {Promise<Tenant>} The approved prospect.
 */
export async function approveTenant(id: string, input: ApproveTenantRequest): Promise<Tenant> {
  const { data } = await httpClient.post<Tenant>(`/sales/tenants/${id}/approve`, input)
  return data
}

export async function archiveTenant(id: string, reason: string): Promise<Tenant> {
  const { data } = await httpClient.post<Tenant>(`/sales/tenants/${id}/archive`, { reason })
  return data
}

/**
 * @description Restores a previously archived tenant with a mandatory reason note.
 * @param {string} id - The tenant id to restore.
 * @param {string} reason - The mandatory reason note.
 * @returns {Promise<Tenant>} The updated tenant.
 */
export async function restoreTenant(id: string, reason: string): Promise<Tenant> {
  const { data } = await httpClient.post<Tenant>(`/sales/tenants/${id}/restore`, { reason })
  return data
}
